/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : VehicleSystemsEditDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Stack } from "@mui/material";
import {
  HandleApiActions,
  adminSettingsAPI,
  labCar,
} from "../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import CustomDialogBoxV2 from "../customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomButtonV2 from "../customComponentsV2/common/button/CustomButtonV2";
import CustomFormTextFieldV2 from "../customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomSelectV2 from "../customComponentsV2/common/select/CustomSelectV2";
import MultiselectConnectorV2 from "../customComponentsV2/common/connectors/MultiselectConnectorV2";
import CustomTabsV2 from "../customComponentsV2/common/tabs/CustomTabsV2";
import AttributesForm from "../../pages/labcar/Attributes/AttributesForm";

const getSelectedVariantIds = (selectedVSItem, selectedSingleVariantDetails) => {
  const activeVariantId = selectedSingleVariantDetails?.variant_id;
  const variantIds = [
    activeVariantId,
    ...(Array.isArray(selectedVSItem?.variant_ids)
      ? selectedVSItem.variant_ids
      : []),
  ].filter((variantId) => variantId !== undefined && variantId !== null);

  return new Set(variantIds.map(String));
};

function VehicleSystemsEditDialog({
  openDialog,
  setOpenDialog,
  title = "Edit Vehicle System",
  viewArchived = false,
  selectedVSItem,
  variantOptionList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  selectedZoneDetails,
  supplierOptionsList,
  vsOptionsList = [],
  getVehicleSystems,
  getZoneVSCountList,
}) {
  const dispatch = useDispatch();
  const adminSettingsDetails = useSelector(
    (state) => state.adminSettingsReducer,
  );

  const [selectedZoneName, setSelectedZoneName] = useState(
    selectedVSItem?.zone_details?.zone_name ||
      selectedZoneDetails?.zone_name ||
      "",
  );
  const [selectedVehicleSystem, setSelectedVehicleSystem] = useState("");
  const [assemblyNo, setAssemblyNo] = useState("");
  const [description, setDescription] = useState("");
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [keywordOptions, setKeywordOptions] = useState([]);
  const [selectedKeywordIds, setSelectedKeywordIds] = useState([]);
  const [reason, setReason] = useState("");
  const [attributeFormData, setAttributeFormData] = useState({
    added: [],
    edited: [],
    deleted: [],
  });
  const [vsEditTabValue, setVsEditTabValue] = useState(0);

  const normalizeSupplierOptions = (list) => {
    if (!Array.isArray(list)) return [];
    return list
      .map((opt) => {
        if (!opt) return null;
        if (typeof opt === "string") return { value: opt, label: opt };
        const value = opt.value ?? opt.id ?? opt.name ?? opt.label;
        const label = opt.label ?? opt.name ?? opt.value ?? opt.id;
        if (value == null || label == null) return null;
        return { value, label };
      })
      .filter(Boolean);
  };

  const suppliersFromProps = useMemo(
    () => normalizeSupplierOptions(supplierOptionsList),
    [supplierOptionsList],
  );
  const suppliersFromStore = useMemo(() => {
    const list = adminSettingsDetails?.adminSettings?.data?.ecu_suppliers;
    return normalizeSupplierOptions(list);
  }, [adminSettingsDetails]);
  const resolvedSupplierOptionsList =
    suppliersFromProps.length > 0 ? suppliersFromProps : suppliersFromStore;

  const normalizeKeywordIds = (keywords) =>
    (Array.isArray(keywords) ? keywords : [])
      .map((keyword) =>
        keyword && typeof keyword === "object"
          ? keyword?.id ?? keyword?.value
          : keyword,
      )
      .filter((id) => id !== null && id !== undefined && id !== "");

  const selectedVariantOptions = useMemo(() => {
    const selectedVariantIds = getSelectedVariantIds(
      selectedVSItem,
      selectedSingleVariantDetails,
    );

    return (variantOptionList || []).filter((variant) =>
      selectedVariantIds.has(String(variant?.value)),
    );
  }, [selectedVSItem, selectedSingleVariantDetails, variantOptionList]);

  useEffect(() => {
    if (!openDialog) return;
    if (resolvedSupplierOptionsList.length > 0) return;

    dispatch(
      HandleApiActions({
        ...adminSettingsAPI.get_admin_settings,
        show_toast: false,
      }),
    );
    setVsEditTabValue(0);
  }, [openDialog, resolvedSupplierOptionsList.length, dispatch]);

  useEffect(() => {
    if (!openDialog) return;

    dispatch(
      HandleApiActions({
        api_name: "/SCM/settings/view/keywords",
        https_method: "GET",
        params: { type: 6 },
        ignore_app_loading_spinner: true,
      }),
    ).then((response) => {
      const keywords = Array.isArray(response?.data) ? response.data : [];
      setKeywordOptions(
        keywords.map((keyword) => ({
          value: keyword?.id,
          label: keyword?.name,
        })),
      );
    });
  }, [dispatch, openDialog]);

  useEffect(() => {
    if (!openDialog || !selectedVSItem) return;
    const initialAssembly = selectedVSItem?.assembly_number || "";
    const initialName = selectedVSItem?.name || "";
    const initialDescription = selectedVSItem?.description || "";
    const initialSuppliers = selectedVSItem?.suppliers || [];
    const initialKeywordIds = normalizeKeywordIds(selectedVSItem?.keywords);
    const initialVariants = selectedVariantOptions.map(
      (variant) => variant?.value,
    );

    setAssemblyNo(initialAssembly);
    setSelectedVehicleSystem(initialName);
    setDescription(initialDescription);
    setSelectedSuppliers(initialSuppliers);
    setSelectedKeywordIds(initialKeywordIds);
    setSelectedVariants(initialVariants);
    const initialZone =
      selectedVSItem?.zone_details?.zone_name ||
      selectedProjectDetails?.project_zones?.find(
        (z) => z?.position === selectedVSItem?.zone_position,
      )?.zone_name ||
      selectedZoneDetails?.zone_name ||
      "";
    setSelectedZoneName(initialZone);
    setReason("");
    setAttributeFormData({ added: [], edited: [], deleted: [] });
    setVsEditTabValue(0);
  }, [
    openDialog,
    selectedVSItem,
    selectedSingleVariantDetails,
    selectedVariantOptions,
    selectedZoneDetails,
    selectedProjectDetails,
  ]);

  const handleVSAttributeAdd = useCallback((data = {}) => {
    const added = Array.isArray(data?.added) ? data.added : [];
    const edited = Array.isArray(data?.edited) ? data.edited : [];
    const deleted = Array.isArray(data?.deleted) ? data.deleted : [];

    setAttributeFormData((prev) => {
      const nextData = { added, edited, deleted };
      return JSON.stringify(prev) === JSON.stringify(nextData)
        ? prev
        : nextData;
    });
  }, []);

  const attributeFormRows = useMemo(() => {
    const addedAttributes = Array.isArray(attributeFormData?.added)
      ? attributeFormData.added
      : [];
    const editedAttributes = Array.isArray(attributeFormData?.edited)
      ? attributeFormData.edited
      : [];
    const deletedAttributeIds = Array.isArray(attributeFormData?.deleted)
      ? attributeFormData.deleted.map(String)
      : [];

    const editedAttributeMap = new Map(
      editedAttributes.map((attribute) => [String(attribute?.id), attribute]),
    );

    const existingAttributes = (selectedVSItem?.attributes || [])
      .filter((attribute) => {
        const attributeId = attribute?.attribute_id ?? attribute?.id;
        return !deletedAttributeIds.includes(String(attributeId));
      })
      .map((attribute) => {
        const attributeId = attribute?.attribute_id ?? attribute?.id;
        const editedAttribute = editedAttributeMap.get(String(attributeId));

        if (!editedAttribute) {
          return attribute;
        }

        return {
          ...attribute,
          attribute_id: attributeId,
          attribute_type: editedAttribute.attribute_type,
          attribute_sub_type: editedAttribute.attribute_sub_type,
          attribute_heading: editedAttribute.attribute_heading,
          attribute_value: editedAttribute.attribute_value,
          edited: true,
        };
      });

    const addedAttributeRows = addedAttributes.map((attribute) => ({
      ...attribute,
      attribute_id: null,
      newlyAdded: true,
      edited: false,
    }));

    return [...existingAttributes, ...addedAttributeRows];
  }, [
    attributeFormData?.added,
    attributeFormData?.deleted,
    attributeFormData?.edited,
    selectedVSItem?.attributes,
  ]);

  let resolvedZoneDetails = null;
  if (selectedProjectDetails?.is_zonal) {
    const zones = selectedProjectDetails?.project_zones;
    resolvedZoneDetails =
      zones?.find(
        (z) => z?.zone_name === selectedZoneName || z?.position === selectedZoneName,
      ) ||
      (selectedZoneDetails?.zone_name
        ? zones?.find((z) => z?.zone_name === selectedZoneDetails?.zone_name)
        : null);
  }

  const isDialogDetailsValid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;
    return (
      (!selectedProjectDetails?.is_zonal || Boolean(selectedZoneName)) &&
      assemblyNo?.trim() !== "" &&
      regexMin3.test(assemblyNo.trim()) &&
      selectedVehicleSystem?.trim()?.length > 0 &&
      description?.trim() !== "" &&
      selectedSuppliers?.length > 0
    );
  };

  const isDialogFieldsValid = () =>
    isDialogDetailsValid() && reason?.trim()?.length >= 3;

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setAttributeFormData({ added: [], edited: [], deleted: [] });
    setReason("");
    setVsEditTabValue(0);
  };

  const handleEditVehicleSystem = async () => {
    const projectId = selectedProjectDetails?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id;
    const vsId = selectedVSItem?.id;
    const addedAttributes = Array.isArray(attributeFormData?.added)
      ? attributeFormData.added
      : [];
    const updatedAttributes = Array.isArray(attributeFormData?.edited)
      ? attributeFormData.edited
      : [];
    const deletedAttributes = Array.isArray(attributeFormData?.deleted)
      ? attributeFormData.deleted
      : [];

    if (!projectId || !variantId || !vsId) return;

    if (
      addedAttributes.length > 0 ||
      updatedAttributes.length > 0 ||
      deletedAttributes.length > 0
    ) {
      await Promise.resolve(dispatch(
        HandleApiActions({
          ...labCar.updateVSAttribute,
          params: {
            entity_id: Number(selectedVSItem?.id),
            new_attributes: addedAttributes,
            updated_attributes: updatedAttributes,
            deleted_attributes: deletedAttributes,
          },
          show_toast: false,
        }),
      ));
    }

    const resp = await Promise.resolve(dispatch(
      HandleApiActions({
        ...labCar.edit_vehicle_system,
        show_toast: true,
        params: {
          project_id: Number(projectId),
          variant_id: Number(variantId),
          vs_id: Number(vsId),
          ...(resolvedZoneDetails && {
            zone_details: {
              zone_name: resolvedZoneDetails?.zone_name,
              position: resolvedZoneDetails?.position,
            },
          }),
          assembly_number: assemblyNo?.trim(),
          name: selectedVehicleSystem,
          description: description,
          suppliers: selectedSuppliers,
          keywords: selectedKeywordIds?.map(Number) || [],
          current_time: new Date().toISOString(),
          reason: reason?.trim(),
        },
      }),
    ));

    if (!resp?.error) {
      handleCloseDialog();
      getVehicleSystems();
      getZoneVSCountList();
    }
  };

  const submitButtonDisabled =
    vsEditTabValue === 0 ? !isDialogDetailsValid() : !isDialogFieldsValid();

  const isAddAction = title === "Draft Vehicle System" || viewArchived;
  let submitButtonName = "Save";
  if (vsEditTabValue === 0) {
    submitButtonName = "Next";
  } else if (isAddAction) {
    submitButtonName = "Add";
  }

  return (
    <CustomDialogBoxV2
      open={openDialog}
      onClose={handleCloseDialog}
      title={title}
      dialogActions={
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box>
            {vsEditTabValue === 1 && (
              <CustomButtonV2
                type="cancel"
                size="small"
                btnName={"Back"}
                handleOnClick={() => {
                  setVsEditTabValue(0);
                }}
              />
            )}
          </Box>
          <CustomButtonV2
            type="submit"
            size="small"
            disabled={submitButtonDisabled}
            btnName={submitButtonName}
            handleOnClick={() => {
              if (vsEditTabValue === 0) {
                setVsEditTabValue(1);
              } else {
                void handleEditVehicleSystem();
              }
            }}
          />
        </Box>
      }
    >
      <CustomTabsV2
        defaultTab={vsEditTabValue}
        value={vsEditTabValue}
        onTabChange={(newValue) => setVsEditTabValue(newValue)}
        tabs={[
          {
            name: "Details",
            content: (
              <Stack spacing={2} mb={2} sx={{ width: "450px" }}>
                {(title === "Draft Vehicle System" || viewArchived) &&
                  title !== "Edit Vehicle System" &&
                  selectedProjectDetails?.is_zonal && (
                    <CustomSelectV2
                      size={"small"}
                      label={"Select Zone"}
                    required={true}
                    optionsList={
                      selectedProjectDetails?.project_zones?.map((zone) => ({
                        value: zone?.zone_name,
                        label: zone?.zone_name,
                      })) || []
                    }
                    selectedValue={selectedZoneName || " "}
                    handleOnChange={(e) => setSelectedZoneName(e.target.value)}
                  />
                )}
                <CustomFormTextFieldV2
                  size={"small"}
                  label={"Assembly No"}
                  required={true}
                  type={"text"}
                  fldType={"textR"}
                  value={assemblyNo}
                  setValue={(value) =>
                    value.length <= 40 && setAssemblyNo(value)
                  }
                  maxLength={40}
                />
                <CustomSelectV2
                  size={"small"}
                  label={"Select Vehicle System"}
                  required={true}
                  optionsList={vsOptionsList}
                  selectedValue={selectedVehicleSystem || " "}
                  handleOnChange={(e) =>
                    setSelectedVehicleSystem(e.target.value)
                  }
                />
                <CustomFormTextFieldV2
                  size={"small"}
                  label={"Description"}
                  required={true}
                  value={description}
                  setValue={(value) =>
                    value.length <= 500 && setDescription(value)
                  }
                  maxLength={500}
                />
                <MultiselectConnectorV2
                  required
                  mutiple
                  size={"small"}
                  label={"Select Suppliers"}
                  optionsList={resolvedSupplierOptionsList?.map((item) => ({
                    value: item?.value?.toUpperCase?.() || item?.value,
                    label: item?.label,
                  }))}
                  selectedValues={selectedSuppliers || []}
                  setSelectedValues={setSelectedSuppliers}
                  placeholder={false}
                />
                <MultiselectConnectorV2
                  mutiple
                  selectedValues={selectedKeywordIds || []}
                  setSelectedValues={setSelectedKeywordIds}
                  label={"Keywords"}
                  optionsList={keywordOptions}
                  placeholder={false}
                />
                <MultiselectConnectorV2
                  mutiple
                  selectedValues={selectedVariants || []}
                  setSelectedValues={setSelectedVariants}
                  label={"Select Variants"}
                  optionsList={selectedVariantOptions}
                  placeholder={false}
                  immutableValues={
                    selectedSingleVariantDetails?.variant_id == null
                      ? []
                      : [selectedSingleVariantDetails?.variant_id]
                  }
                />

              </Stack>
            ),
          },
          {
            name: "Attribute",
            content: (
              <Stack
                spacing={2}
                mb={2}
                justifyContent={"center"}
                sx={{ width: "450px" }}
              >
                <AttributesForm
                  type={"EDIT"}
                  attributeFromApi={attributeFormRows}
                  getFormData={handleVSAttributeAdd}
                  reason={reason}
                  setReason={setReason}
                  showReason={true}
                />
              </Stack>
            ),
          },
        ]}
      />
    </CustomDialogBoxV2>
  );
}


VehicleSystemsEditDialog.propTypes = {
  title: PropTypes.string,
  viewArchived: PropTypes.bool,
  supplierOptionsList: PropTypes.array,
  vsOptionsList: PropTypes.array,
  getVehicleSystems: PropTypes.func,
  getZoneVSCountList: PropTypes.func,
  openDialog: PropTypes.bool,
  setOpenDialog: PropTypes.func,
  selectedVSItem: PropTypes.shape({
    id: PropTypes.any,
    attributes: PropTypes.array,
    keywords: PropTypes.array,
    assembly_number: PropTypes.any,
    name: PropTypes.any,
    description: PropTypes.any,
    suppliers: PropTypes.any,
    zone_details: PropTypes.object,
    zone_position: PropTypes.any,
  }),
  variantOptionList: PropTypes.array,
  selectedProjectDetails: PropTypes.shape({
    project_id: PropTypes.any,
    is_zonal: PropTypes.bool,
    project_zones: PropTypes.array,
  }),
  selectedSingleVariantDetails: PropTypes.shape({
    variant_id: PropTypes.any,
  }),
  selectedZoneDetails: PropTypes.shape({
    zone_name: PropTypes.any,
    position: PropTypes.any,
  }),
};

export default VehicleSystemsEditDialog;
